import React, { useState, useEffect, useRef } from 'react';
import { Building2, X, Search, ChevronDown, Loader2, Plus } from 'lucide-react';
import { toast } from 'react-hot-toast';
import ModalOverlay from '../../../components/ui/ModalOverlay';
import { BRAND_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';
import { clientService } from '../api/clientsApi';
import { userService } from '../../users';
import { getInitials, getAvatarColor, INDUSTRIES, fieldCls } from './clientHelpers';

const AddClientModal = ({ isOpen, onClose, onCreated }) => {
  const [clientUsers, setClientUsers]   = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearch, setUserSearch]     = useState('');
  const [userDropOpen, setUserDropOpen] = useState(false);
  const [pickedUser, setPickedUser]     = useState(null);
  const [form, setForm] = useState({ organization: '', industry: '', contact_phone: '', address: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const dropRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setLoadingUsers(true);
    userService.getAllUsers()
      .then(res => {
        const users = Array.isArray(res) ? res : (res?.users || []);
        setClientUsers(users.filter(u => u.role === 'client'));
      })
      .catch(() => toast.error('Could not load users'))
      .finally(() => setLoadingUsers(false));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setPickedUser(null);
      setForm({ organization: '', industry: '', contact_phone: '', address: '', notes: '' });
      setUserSearch('');
    }
  }, [isOpen]);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setUserDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredUsers = clientUsers.filter(u =>
    u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pickedUser) { toast.error('Select a user'); return; }
    if (!form.organization.trim()) { toast.error('Organization name is required'); return; }
    setSaving(true);
    try {
      const created = await clientService.createOrUpdateClient({ user_id: pickedUser.id, ...form });
      toast.success('Client profile created');
      onCreated(created);
      onClose();
    } catch {
      toast.error('Failed to create client');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalOverlay isOpen={isOpen} onClose={onClose} closeOnOverlayClick zIndex={50}>
      <div
        className="rounded-lg shadow-2xl w-full max-w-md sm:max-w-lg mx-4 flex flex-col max-h-[90vh] overflow-hidden"
        style={{ backgroundColor: SYSTEM_COLORS.shellSurface }}
      >
        {/* Header */}
        <div className="flex items-center justify-between flex-shrink-0 px-5 py-4" style={{ backgroundColor: BRAND_COLORS.primary }}>
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center rounded-lg w-7 h-7 bg-white/20">
              <Building2 size={13} className="text-white" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-white">Add Client Profile</h2>
              <p className="text-[10px] text-white/70">Link org details to a client account</p>
            </div>
          </div>
          <button onClick={onClose} className="flex items-center justify-center rounded-lg w-7 h-7 bg-white/10 hover:bg-white/20">
            <X size={14} className="text-white" />
          </button>
        </div>

        {/* Form body — scrolls independently */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* User picker */}
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
              Client User Account <span className="text-red-400">*</span>
            </label>
            <div ref={dropRef} className="relative">
              <div
                onClick={() => setUserDropOpen(o => !o)}
                className="flex items-center w-full gap-2 px-3 py-2 text-sm bg-white border border-gray-200 cursor-pointer rounded-xl hover:border-gray-300"
              >
                {pickedUser ? (
                  <>
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0"
                      style={{ backgroundColor: getAvatarColor(pickedUser.name) }}>
                      {getInitials(pickedUser.name)}
                    </div>
                    <span className="flex-1 font-medium text-gray-800 truncate">{pickedUser.name}</span>
                    <span className="text-xs text-gray-400 truncate">{pickedUser.email}</span>
                  </>
                ) : (
                  <span className="flex-1 text-gray-400">{loadingUsers ? 'Loading users…' : 'Select client user…'}</span>
                )}
                <ChevronDown size={13} className="flex-shrink-0 text-gray-400" />
              </div>
              {userDropOpen && (
                <div className="absolute z-20 w-full mt-1 overflow-hidden bg-white border shadow-xl rounded-xl" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                  <div className="p-2 border-b" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                    <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-gray-50">
                      <Search size={12} className="text-gray-400" />
                      <input
                        autoFocus
                        value={userSearch}
                        onChange={e => setUserSearch(e.target.value)}
                        placeholder="Search by name or email…"
                        className="w-full text-xs bg-transparent outline-none"
                        onClick={e => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  <ul className="py-1 overflow-y-auto max-h-44">
                    {filteredUsers.length === 0 ? (
                      <li className="px-4 py-3 text-xs text-center text-gray-400">No client-role users found</li>
                    ) : filteredUsers.map(u => (
                      <li key={u.id}
                        onClick={() => { setPickedUser(u); setUserDropOpen(false); }}
                        className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-gray-50"
                      >
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0"
                          style={{ backgroundColor: getAvatarColor(u.name) }}>
                          {getInitials(u.name)}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-gray-800 truncate">{u.name}</p>
                          <p className="text-[10px] text-gray-400 truncate">{u.email}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Organization */}
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
              Organization <span className="text-red-400">*</span>
            </label>
            <input value={form.organization} onChange={e => setForm(f => ({ ...f, organization: e.target.value }))}
              placeholder="e.g. Acme Corp" className={fieldCls} required />
          </div>

          {/* Industry */}
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Industry</label>
            <select value={form.industry} onChange={e => setForm(f => ({ ...f, industry: e.target.value }))} className={fieldCls}>
              <option value="">— Select industry —</option>
              {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>

          {/* Phone + Address */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Phone</label>
              <input value={form.contact_phone} onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))}
                placeholder="+255 7XX XXX XXX" className={fieldCls} />
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Address</label>
              <input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                placeholder="City, Country" className={fieldCls} />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Notes</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={3} placeholder="Any additional context…" className={fieldCls} />
          </div>
        </form>

        {/* Footer */}
        <div className="flex justify-end flex-shrink-0 gap-2 px-5 py-4 border-t"
          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 rounded-xl hover:bg-gray-100">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
            style={{ backgroundColor: BRAND_COLORS.primary }}>
            {saving ? <><Loader2 size={13} className="animate-spin" /> Creating…</> : <><Plus size={13} /> Create Client</>}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
};

export default AddClientModal;
